import React, { useMemo } from "react";
import { Stack, Typography, Box, Skeleton } from "@mui/material";

import TaskItem from "./TaskItem";

/**
 * Lista de tareas agrupada por estado: Overdue, Due Today, Other Active, Completed.
 */
export default function TaskList({ tasks, loading, onComplete, onCancel, onDelete, onEdit }) {
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const grouped = useMemo(() => {
    const overdue = [];
    const dueToday = [];
    const otherActive = [];
    const completed = [];

    (tasks || []).forEach((t) => {
      if (t.status === "completed" || t.status === "cancelled") {
        completed.push(t);
      } else if (t.dueDate && t.dueDate < today) {
        overdue.push(t);
      } else if (t.dueDate === today) {
        dueToday.push(t);
      } else {
        otherActive.push(t);
      }
    });

    return { overdue, dueToday, otherActive, completed: completed.slice(0, 5) };
  }, [tasks, today]);

  if (loading) {
    return (
      <Stack spacing={1}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={44} />
        ))}
      </Stack>
    );
  }

  const noTasks =
    grouped.overdue.length === 0 &&
    grouped.dueToday.length === 0 &&
    grouped.otherActive.length === 0 &&
    grouped.completed.length === 0;

  if (noTasks) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No tasks for today — great job!
        </Typography>
      </Box>
    );
  }

  const commonProps = { onComplete, onCancel, onDelete, onEdit };

  return (
    <Stack
      spacing={0.5}
      sx={{
        maxHeight: 380,
        overflowY: "auto",
        pr: 0.5,
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": { bgcolor: "#E2E8F0", borderRadius: 2 },
      }}
    >
      {/* Overdue */}
      {grouped.overdue.length > 0 && (
        <Section label="Overdue" color="#DC2626">
          {grouped.overdue.map((t) => (
            <TaskItem key={t.id} task={t} {...commonProps} />
          ))}
        </Section>
      )}

      {/* Due Today */}
      {grouped.dueToday.length > 0 && (
        <Section label="Due Today" color="#D97706">
          {grouped.dueToday.map((t) => (
            <TaskItem key={t.id} task={t} {...commonProps} />
          ))}
        </Section>
      )}

      {/* Other Active */}
      {grouped.otherActive.length > 0 && (
        <Section label="Upcoming" color="#2563EB">
          {grouped.otherActive.map((t) => (
            <TaskItem key={t.id} task={t} {...commonProps} />
          ))}
        </Section>
      )}

      {/* Completed (recent) */}
      {grouped.completed.length > 0 && (
        <Section label="Completed" color="#16A34A">
          {grouped.completed.map((t) => (
            <TaskItem key={t.id} task={t} {...commonProps} />
          ))}
        </Section>
      )}
    </Stack>
  );
}

function Section({ label, color, children }) {
  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{
          fontSize: "0.62rem",
          textTransform: "uppercase",
          letterSpacing: 0.8,
          color,
          mb: 0.5,
          display: "block",
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}
